// Canonical user birthday map for character awareness in AI prompts.
// Hardcoded for the small two-user platform (Haley + Nicole). If the user
// base ever grows, move this to profiles.birthday.

export const USER_BIRTHDAYS = {
  // Haley
  '6db164e7-0419-470c-a8fe-2ddb899f5578': { name: 'Haley', month: 5,  day: 6,  year: 1995 },
  // Nicole
  '8420ef71-0898-41e0-ac6c-6916969b82f2': { name: 'Nicole', month: 8, day: 15, year: 1995 },
};

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function fmtDate(month, day) {
  return `${MONTH_NAMES[month - 1]} ${day}`;
}

// The age they turn on THIS calendar year's birthday. Whether the year to
// report is this one or the next is the caller's question, not this one's.
function ageOnBirthday(birthYear, today) {
  return today.getFullYear() - birthYear;
}

function daysUntil(month, day, today) {
  const thisYear = new Date(today.getFullYear(), month - 1, day);
  let target = thisYear;
  if (target < today && !(target.getMonth() === today.getMonth() && target.getDate() === today.getDate())) {
    target = new Date(today.getFullYear() + 1, month - 1, day);
  }
  const ms = target - new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round(ms / 86400000);
}

/**
 * Build a system-prompt section that makes the AI character aware of player
 * birthdays. Designed to be injected into DM, RP, channel, and phone-call
 * system prompts so characters can bring birthdays up organically.
 *
 * @param {string|null|undefined} viewerUserId - The user the character is talking to
 * @param {Date} [today] - Defaults to now; pass for testing
 * @returns {string} System-prompt section, or empty string if no entries
 */
export function buildBirthdayContext(viewerUserId, today = new Date()) {
  const entries = Object.entries(USER_BIRTHDAYS);
  if (entries.length === 0) return '';

  const todayMonth = today.getMonth() + 1;
  const todayDay = today.getDate();
  const todayLabel = `${MONTH_NAMES[todayMonth - 1]} ${todayDay}, ${today.getFullYear()}`;

  const lines = [];
  lines.push(`═══ PLAYER BIRTHDAY AWARENESS ═══`);
  lines.push(`Today is ${todayLabel}.`);
  lines.push('');

  const viewer = viewerUserId ? USER_BIRTHDAYS[viewerUserId] : null;

  // ONLY THE PERSON IN THE ROOM. Haley, live 2026-08-09: "they keep saying her
  // bday is coming up which is right but they keep saying haley's bday even
  // though it's nicole's bday on august 15th … please make sure that our
  // details are all separate."
  //
  // This block used to list BOTH players every turn and then carried a
  // CROSS-MENTIONS instruction telling characters to raise the other one's
  // birthday, with worked examples — "I should call Haley today, it's her day"
  // and "Tell Nicole her birthday is coming up, I haven't forgotten". On
  // 2026-08-09, six days before Nicole's birthday, that put "birthday is in 6
  // days" and the string "Haley" in the same short section with an explicit
  // instruction to say one about the other. The model did exactly what it was
  // told and congratulated the wrong person.
  //
  // A character now only ever learns the birthday of the player they are
  // actually talking to. If we cannot identify that player, the section is
  // omitted entirely rather than guessed — a wrong name is worse than silence.
  if (!viewer) return '';

  for (const [, person] of entries.filter(([, p]) => p.name === viewer.name)) {
    const birthdayLabel = fmtDate(person.month, person.day);
    const ageNow = ageOnBirthday(person.year, today);
    // If this year's birthday is already behind us, every "will turn" below
    // refers to NEXT year's birthday — one year older. Both branches of this
    // ternary said `ageNow` from the day it was written (found 2026-08-12 by
    // reading the unused-variable warnings for dropped wires), so after each
    // May 6th every surface told Haley's characters she was turning a year
    // YOUNGER than the truth. On the birthday itself it is not "passed":
    // "turning ${age}" today must be this year's age.
    const birthdayPassedThisYear = todayMonth > person.month
      || (todayMonth === person.month && todayDay > person.day);
    const ageOnTheirBirthday = birthdayPassedThisYear ? ageNow + 1 : ageNow;
    const isToday = todayMonth === person.month && todayDay === person.day;
    const inDays = daysUntil(person.month, person.day, today);

    let statusBit;
    if (isToday) {
      statusBit = `🎂 IT IS THEIR BIRTHDAY TODAY. They are turning ${ageOnTheirBirthday}.`;
    } else if (inDays <= 7) {
      statusBit = `Their birthday is in ${inDays} day${inDays === 1 ? '' : 's'} (they will turn ${ageOnTheirBirthday}).`;
    } else if (inDays <= 30) {
      statusBit = `Their birthday is coming up in ${inDays} days (they will turn ${ageOnTheirBirthday}).`;
    } else {
      statusBit = `Their birthday is ${birthdayLabel} (about ${inDays} days away; they will turn ${ageOnTheirBirthday}).`;
    }

    lines.push(`- ${person.name} — the person you are talking to right now: born ${birthdayLabel} ${person.year}. ${statusBit}`);
  }

  lines.push('');
  lines.push(`HOW TO USE THIS: Acknowledge the birthday the way YOUR character would — in-character wishes, teasing, gift offers, romantic gestures, or staying quiet about it if that fits. Bring it up ONLY at a natural beat, not as a forced opener. If today is their birthday, prioritize an organic acknowledgment in your first response, then carry on the scene. If their birthday is days away, you may casually reference it. If it's months out, only mention it if the topic of birthdays / dates / time-passing comes up. NEVER include a literal date string ("May 6, 2026") in your dialogue — speak about it the way a friend or lover would.

WHOSE BIRTHDAY THIS IS: ${viewer.name}'s, and ONLY ${viewer.name}'s. This date belongs to the person in front of you. Do not attach it to anyone else, do not wish a different person a happy birthday, and do not name another player when you speak about it. If you are not certain whose birthday it is, say nothing about birthdays at all.`);

  return lines.join('\n');
}
